import type { TreeNodeStyleConfig, ContentContainerConfig } from '../types/SessionTreeProps'

interface RequiredNodeConfig {
  readonly host: TreeNodeStyleConfig
  readonly project: TreeNodeStyleConfig
  readonly session: TreeNodeStyleConfig
}

interface TreeDefaults {
  readonly nodeConfig: RequiredNodeConfig
  readonly contentContainerConfig: ContentContainerConfig
}

interface ThemedTreeDefaults {
  readonly dark: TreeDefaults
  readonly light: TreeDefaults
}

const THEMED_TREE_PROPS: ThemedTreeDefaults = {
  dark: {
    nodeConfig: {
      host: {
        fontSize: '1.35rem',
        fontWeight: 500,
        verticalGap: 2,
        color: 'rgba(255, 255, 255, 0.3)',
        iconColor: 'rgba(255, 255, 255, 0.3)',
        wrapper: {
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          borderRadius: 1,
          padding: 2.5,
          marginBottom: 2,
        },
      },
      project: {
        fontSize: '1.15rem',
        fontWeight: 500,
        verticalGap: 2,
        color: 'rgba(255, 255, 255, 0.5)',
        iconColor: 'rgba(255, 255, 255, 0.5)',
        wrapper: {
          backgroundColor: 'rgba(255, 255, 255, 0.025)',
          borderRadius: 1,
          padding: 2,
          marginBottom: 1.5,
        },
      },
      session: {
        fontSize: '1rem',
        fontWeight: 400,
        verticalGap: 1.5,
        color: 'rgba(255, 255, 255, 0.7)',
        iconColor: 'rgba(255, 255, 255, 0.7)',
        wrapper: {
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          borderRadius: 1,
          padding: 2.5,
          marginBottom: 1.5,
        },
      },
    },
    contentContainerConfig: {
      padding: 1.5,
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: 'divider',
      borderRadius: 1,
      backgroundColor: 'rgba(255, 255, 255, 0.01)',
      marginTop: 1,
      marginLeft: 'calc(1.5rem + 8px)',
    },
  },
  light: {
    nodeConfig: {
      host: {
        fontSize: '1.35rem',
        fontWeight: 500,
        verticalGap: 2,
        color: 'rgba(62, 39, 35, 0.5)',
        iconColor: 'rgba(62, 39, 35, 0.5)',
        wrapper: {
          backgroundColor: 'rgba(62, 39, 35, 0.04)',
          borderRadius: 1,
          padding: 2.5,
          marginBottom: 2,
        },
      },
      project: {
        fontSize: '1.15rem',
        fontWeight: 500,
        verticalGap: 2,
        color: 'rgba(62, 39, 35, 0.65)',
        iconColor: 'rgba(62, 39, 35, 0.65)',
        wrapper: {
          backgroundColor: 'rgba(62, 39, 35, 0.025)',
          borderRadius: 1,
          padding: 2,
          marginBottom: 1.5,
        },
      },
      session: {
        fontSize: '1rem',
        fontWeight: 400,
        verticalGap: 1.5,
        color: 'rgba(62, 39, 35, 0.8)',
        iconColor: 'rgba(62, 39, 35, 0.8)',
        wrapper: {
          backgroundColor: 'rgba(62, 39, 35, 0.02)',
          borderRadius: 1,
          padding: 2.5,
          marginBottom: 1.5,
        },
      },
    },
    contentContainerConfig: {
      padding: 1.5,
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: 'divider',
      borderRadius: 1,
      backgroundColor: 'rgba(62, 39, 35, 0.01)',
      marginTop: 1,
      marginLeft: 'calc(1.5rem + 8px)',
    },
  },
} as const

export function getTreeDefaults(mode: 'light' | 'dark'): TreeDefaults {
  return THEMED_TREE_PROPS[mode]
}

/** @deprecated Use getTreeDefaults(mode) for theme-aware defaults */
export const DEFAULT_TREE_PROPS: TreeDefaults = THEMED_TREE_PROPS.dark
